<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Header from '../../app/layout/Header.svelte';
  import { listCategories } from '../../data/categories';
  import { listExpensesByCategoryAndDate, deleteExpense } from '../../data/expenses';
  import type { Expense } from '../../data/types';
  import { formatDateJP } from '../../lib/date';
  import { formatCurrencyJPY } from '../../lib/format';

  interface Props {
    params: { categoryId: string; date: string };
  }

  let { params }: Props = $props();

  let categoryName = $state('カテゴリ');
  let expenses = $state<Expense[]>([]);
  let loading = $state(true);
  let errorMessage = $state('');

  const total = $derived(expenses.reduce((sum, e) => sum + e.amount, 0));

  async function loadData() {
    loading = true;
    errorMessage = '';
    try {
      const [categories, list] = await Promise.all([
        listCategories(),
        listExpensesByCategoryAndDate(params.categoryId, params.date),
      ]);
      const category = categories.find((c) => c.id === params.categoryId);
      categoryName = category?.name ?? 'カテゴリ';
      expenses = list;
    } catch (err) {
      console.error(err);
      errorMessage = 'データの読み込みに失敗しました。';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadData();
  });

  function goToEdit(expenseId: string) {
    push(`/category/${params.categoryId}/expense/${expenseId}/edit`);
  }

  function goToAdd() {
    push(`/category/${params.categoryId}/expense/new?date=${params.date}`);
  }

  async function handleDelete(expenseId: string) {
    const confirmed = confirm('この支出を削除しますか？');
    if (!confirmed) return;
    try {
      await deleteExpense(expenseId);
      await loadData();
    } catch (err) {
      console.error(err);
      errorMessage = '削除に失敗しました。';
    }
  }
</script>

<Header
  title={`${categoryName} - ${formatDateJP(params.date)}`}
  showBack={true}
  backTo={`/category/${params.categoryId}`}
>
  {#snippet right()}
    <button type="button" class="add-button" onclick={goToAdd}>追加</button>
  {/snippet}
</Header>

<main class="page">
  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  <div class="total-row">
    <span class="total-row__label">この日の合計</span>
    <span class="total-row__value">{formatCurrencyJPY(total)}</span>
  </div>

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else if expenses.length === 0}
    <p class="muted-text">この日の支出記録はありません。</p>
  {:else}
    <ul class="expense-list">
      {#each expenses as expense (expense.id)}
        <li class="expense-row">
          <div class="expense-row__main">
            <span class="expense-row__amount">{formatCurrencyJPY(expense.amount)}</span>
            {#if expense.memo}
              <span class="expense-row__memo">{expense.memo}</span>
            {/if}
            {#if expense.paymentMethod}
              <span class="expense-row__payment">{expense.paymentMethod}</span>
            {/if}
          </div>
          <div class="expense-row__actions">
            <button type="button" class="icon-button" onclick={() => goToEdit(expense.id)}>
              編集
            </button>
            <button
              type="button"
              class="icon-button icon-button--danger"
              onclick={() => handleDelete(expense.id)}
            >
              削除
            </button>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 640px;
    margin: 0 auto;
  }

  .add-button {
    padding: 0.45rem 0.8rem;
    border-radius: var(--radius-md);
    border: none;
    background-color: var(--color-primary);
    color: var(--color-primary-text);
    cursor: pointer;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  .add-button:hover,
  .add-button:focus-visible {
    opacity: 0.9;
  }

  .error-text {
    color: var(--color-danger);
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
  }

  .total-row__label {
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }

  .total-row__value {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--color-text);
  }

  .expense-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .expense-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
  }

  .expense-row__main {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .expense-row__amount {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--color-text);
  }

  .expense-row__memo {
    font-size: 0.85rem;
    color: var(--color-text);
    overflow-wrap: break-word;
  }

  .expense-row__payment {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .expense-row__actions {
    display: flex;
    gap: 0.4rem;
    flex-shrink: 0;
  }

  .icon-button {
    padding: 0.4rem 0.7rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
    cursor: pointer;
    font-size: 0.8rem;
  }

  .icon-button:hover,
  .icon-button:focus-visible {
    border-color: var(--color-primary);
  }

  .icon-button--danger {
    color: var(--color-danger);
    border-color: var(--color-danger);
    background-color: var(--color-danger-bg);
  }
</style>
